import React, { useContext, useEffect, useState } from 'react';

import DatepickerCombinedComponent from 'component/Search/Datepicker/Combined';
import DatepickerSplitComponent from 'component/Search/Datepicker/Split';
import {
    ActiveDatepicker,
    DatepickerType,
    FormType,
    ValueChangeCombinedParams,
    ValueChangeParams,
} from 'component/Search/Datepicker/types';
import { ActiveDurationLayer } from 'component/Search/Duration/types';
import { CustomSearchInputClassList } from 'component/Search/Input/types';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { useDepartureDate, useReturnDate } from 'hooks/form/search';

import {
    crossValidationErrorAtom,
    refreshNoteVisibility,
} from 'data/Atom/Component';
import { changedTravelDateAtom } from 'data/Atom/IntegratedOvernight';

import { ValidationType } from 'types/Search/form';

import { ToggleFormElementContext } from '../../__context/toggleFormElementProvider';
import {
    CrossValidationError,
    useCrossDateValidation,
} from '../../__hooks/useCrossDateValidation';

const MemoizedDatepickerSplitComponent = React.memo(DatepickerSplitComponent);
const MemoizedDatepickerCombinedComponent = React.memo(
    DatepickerCombinedComponent
);

interface DatepickerProps {
    formType: FormType;
    initialValue?: {
        departureDate?: Date;
        returnDate?: Date;
    };
    label: string[];
    placeholder: string[];
    customClassList?: CustomSearchInputClassList;
    type?: DatepickerType;
    onValidate?: (type: ValidationType, isEmpty: boolean) => void;
}

const Datepicker = ({
    formType,
    initialValue,
    label,
    placeholder,
    customClassList,
    type,
    onValidate,
}: DatepickerProps) => {
    const setShowHighlightData = useSetAtom(changedTravelDateAtom);
    const [departureDate, setDepartureDate] = useDepartureDate();
    const [returnDate, setReturnDate] = useReturnDate();
    const showHighlightData = useAtomValue(changedTravelDateAtom);
    const { setActiveDurationLayer, setActiveDatepicker } = useContext(
        ToggleFormElementContext
    );
    const [error, setError] = useAtom(crossValidationErrorAtom);
    const setRefreshNote = useSetAtom(refreshNoteVisibility);
    const { validateTravelPeriod, openDurationOnEmpty } =
        useCrossDateValidation();
    const [activeMonth, setActiveMonth] = useState(
        initialValue?.departureDate ?? initialValue?.returnDate ?? new Date()
    );
    const crossValidateSelection = (from: Date, to: Date) => {
        const validationResult = validateTravelPeriod(from, to);
        setError(validationResult);
        if (openDurationOnEmpty) {
            setActiveDurationLayer(ActiveDurationLayer.DurationLayer);
        }
    };

    const crossValidateCombinedSelection = (from: Date, to?: Date) => {
        const validationResult = validateTravelPeriod(from, to);
        setError(validationResult);
        if (openDurationOnEmpty) {
            setActiveDurationLayer(ActiveDurationLayer.DurationLayer);
        }
    };

    useEffect(() => {
        if (
            error === CrossValidationError.DurationLonger ||
            error === CrossValidationError.DurationLongerFromDatepicker
        ) {
            setActiveDatepicker(ActiveDatepicker.Return);
        }
        if (error === CrossValidationError.NoError) {
            setActiveDurationLayer(ActiveDurationLayer.None);
        }
    }, [error, setActiveDatepicker, setActiveDurationLayer]);

    const onDatepickerOpen = (value: Date) => {
        setActiveDurationLayer(ActiveDurationLayer.None);
        setActiveMonth(value);
    };
    const onClose = () => {
        setShowHighlightData(false);
    };

    const onDatepickerChange = (value: ValueChangeParams) => {
        crossValidateSelection(value.from, value.to);
        setDepartureDate(value.from);
        setReturnDate(value.to);
    };

    const onCombinedDatepickerChange = (value: ValueChangeCombinedParams) => {
        crossValidateCombinedSelection(value.from, value.to);
        setDepartureDate(value.from);
        setReturnDate(value.to);
    };

    useEffect(() => {
        if (initialValue?.departureDate) {
            setDepartureDate(initialValue.departureDate);
            setRefreshNote(false);
        }
        if (initialValue?.returnDate) {
            setReturnDate(initialValue.returnDate);
            setRefreshNote(false);
        }
    }, [
        initialValue?.departureDate,
        initialValue?.returnDate,
        setDepartureDate,
        setReturnDate,
        setRefreshNote,
    ]);

    return type === DatepickerType.Split ? (
        <MemoizedDatepickerSplitComponent
            formType={formType}
            dateRange={{
                from: departureDate,
                to: returnDate,
            }}
            placeholder={placeholder}
            label={label}
            customClassList={customClassList}
            showHighlightData={showHighlightData}
            activeMonth={activeMonth}
            onValidate={onValidate}
            callback={{
                onValueChange: onDatepickerChange,
                onClose: () => {
                    onClose();
                },
                onOpen: onDatepickerOpen,
                onMonthChange: setActiveMonth,
            }}
        />
    ) : (
        DatepickerType.Combined && (
            <MemoizedDatepickerCombinedComponent
                formType={formType}
                dateRange={{
                    from: departureDate,
                    to: returnDate,
                }}
                placeholder={placeholder}
                label={label}
                customClassList={customClassList}
                showHighlightData={showHighlightData}
                error={error !== CrossValidationError.NoError}
                onValidate={onValidate}
                callback={{
                    onValueChange: onCombinedDatepickerChange,
                    onClose: () => {
                        setShowHighlightData(false);
                    },
                    onOpen: () => {
                        setActiveDurationLayer(ActiveDurationLayer.None);
                    },
                }}
            />
        )
    );
};

export default Datepicker;
